function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Decimal from 'decimal.js';
import { defaultBillingDayOfWeek } from "../../constants";
import { utils } from "../../utils";
import { DiscountDTOType, serviceCostModel, serviceModel, serviceTemplateBundleModel, timeModel } from '..';
import { ApprovalMethodDateModel } from "../ApprovalMethodDateModel";
import { BillingDayOfMonthModel } from "../BillingDayOfMonth";
import { LimitedTimeDiscountOfferModel } from "../LimitedTimeDiscountOfferModel";
import * as serviceBillingModel from "../service-billing";
import { makeBillUpfrontState, makeOnApprovalBilling, makeOneTimeInStagesBilling, makeOneTimeManualBilling, makeOngoingBilling, makeRepeatableManualBilling } from "../service-billing/makers";
import { matchBilling } from "../service-template";
import { ServiceIntegrationsModel } from "../ServiceIntegrations";
import { toDateStampObject } from "../time";
import * as makers from "./makers";
import { ServiceStatusType } from "./model";
import * as selectors from "./selectors";
export function includedServiceFromDTO(dto) {
  return _objectSpread(_objectSpread({}, dto), {}, {
    rate: new Decimal(dto.rate).div(100),
    integrations: ServiceIntegrationsModel.fromDTO(dto.integrations)
  });
}
export function includedServiceToDTO(service) {
  return _objectSpread(_objectSpread({}, service), {}, {
    rate: service.rate.mul(100).toNumber(),
    integrations: service.integrations.toJSON()
  });
}
export function fromDTO(service) {
  const status = statusFromDTO(service.status);
  const billing = serviceBillingModel.fromDTO(service.billing, selectors.isAwaitingApproval(status));
  const limitedTimeDiscountOffer = service.status.type === ServiceStatusType.AwaitingApproval ? service.status.awaitingApproval.limitedTimeDiscountOffer : undefined;
  if (limitedTimeDiscountOffer) {
    service.cost.discount = limitedTimeDiscountOffer.discount;
  }
  const cost = serviceCostModel.fromDTO(service.cost);
  if (billing != null) {
    return makers.makeService(_objectSpread(_objectSpread({}, service), {}, {
      billing,
      cost,
      status,
      participation: !service.participation.type ? makers.makeMandatoryParticipation() : service.participation,
      integrations: ServiceIntegrationsModel.fromDTO(service.integrations),
      bundle: service.bundle ? {
        services: service.bundle.services.map(service => includedServiceFromDTO(service))
      } : undefined
    }));
  }
}
export function toDTO(s) {
  const limitedTimeDiscountOffer = serviceModel.limitedTimeDiscountOffer(s);
  const dto = _objectSpread({
    id: s.id,
    name: s.name,
    description: s.description,
    serviceTemplateId: s.serviceTemplateId,
    participation: s.participation,
    isBillable: s.isBillable,
    cost: serviceCostModel.toDTO({
      cost: s.cost,
      shouldResetDiscount: !!limitedTimeDiscountOffer
    }),
    billing: serviceBillingModel.toDTO(s.billing),
    status: statusToDTO(s.status, s.cost),
    source: s.source
  }, s.bundle && {
    bundle: {
      services: s.bundle.services.map(service => includedServiceToDTO(service))
    }
  });
  if (s.integrations.hasIntegrations()) {
    dto.integrations = s.integrations.toJSON();
  }
  return dto;
}
export function fromServiceTemplate(template, source) {
  let context = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const {
    id,
    name,
    description,
    cost,
    integrations,
    bundle
  } = template;
  const status = makers.makeAwaitingApprovalStatus();
  const billing = matchBilling(template.billing, {
    ongoing: b => {
      var _context$billingDayOf, _context$billingDayOf2, _context$isPaused, _context$prorateFirst, _context$isBilledUpfr;
      return makeOngoingBilling(b.recurrencePeriod, {
        billingDayOfMonth: (_context$billingDayOf = context.billingDayOfMonth) !== null && _context$billingDayOf !== void 0 ? _context$billingDayOf : BillingDayOfMonthModel.make(),
        billingDayOfWeek: (_context$billingDayOf2 = context.billingDayOfWeek) !== null && _context$billingDayOf2 !== void 0 ? _context$billingDayOf2 : defaultBillingDayOfWeek,
        isPaused: (_context$isPaused = context.isPaused) !== null && _context$isPaused !== void 0 ? _context$isPaused : false,
        isProrated: (_context$prorateFirst = context.prorateFirstInvoice) !== null && _context$prorateFirst !== void 0 ? _context$prorateFirst : false,
        isBilledUpfront: (_context$isBilledUpfr = context.isBilledUpfront) !== null && _context$isBilledUpfr !== void 0 ? _context$isBilledUpfr : false,
        billUpfrontState: context.isBilledUpfront ? makeBillUpfrontState(context.skipBillingOnAcceptance || !!context.isPaused) : undefined,
        isAwaitingApproval: status.type === ServiceStatusType.AwaitingApproval
      });
    },
    repeatableManual: b => {
      var _context$billingDayOf3, _context$billingDayOf4, _context$isBilledUpfr2;
      return makeRepeatableManualBilling(b.recurrencePeriod, {
        billingDayOfMonth: (_context$billingDayOf3 = context.billingDayOfMonth) !== null && _context$billingDayOf3 !== void 0 ? _context$billingDayOf3 : BillingDayOfMonthModel.make(),
        billingDayOfWeek: (_context$billingDayOf4 = context.billingDayOfWeek) !== null && _context$billingDayOf4 !== void 0 ? _context$billingDayOf4 : defaultBillingDayOfWeek,
        isBilledUpfront: (_context$isBilledUpfr2 = context.isBilledUpfront) !== null && _context$isBilledUpfr2 !== void 0 ? _context$isBilledUpfr2 : false
      });
    },
    oneTimeManual: () => makeOneTimeManualBilling(),
    oneTimeInStages: b => makeOneTimeInStagesBilling(b.oneTimeInStages),
    onApproval: () => makeOnApprovalBilling()
  });
  return makers.makeService({
    name,
    description,
    serviceTemplateId: id,
    integrations,
    status,
    source,
    cost,
    billing,
    bundle: bundle ? serviceTemplateBundleModel.toServiceBundle(bundle) : undefined
  });
}
export function statusFromDTO(dto) {
  if (dto.type === ServiceStatusType.AwaitingApproval) {
    const limitedTimeDiscountOffer = dto.awaitingApproval.limitedTimeDiscountOffer ? LimitedTimeDiscountOfferModel.fromDTO(dto.awaitingApproval.limitedTimeDiscountOffer) : undefined;
    return {
      type: dto.type,
      awaitingApproval: _objectSpread({
        addedOn: timeModel.fromDateStampObject(dto.awaitingApproval.addedOn),
        approvalMethod: ApprovalMethodDateModel.fromDTO(dto.awaitingApproval.approvalMethod),
        note: dto.awaitingApproval.note,
        effectiveDateSource: dto.awaitingApproval.effectiveDateSource || null,
        effectiveDate: dto.awaitingApproval.effectiveDate ? timeModel.fromDateStampObject(dto.awaitingApproval.effectiveDate) : null
      }, limitedTimeDiscountOffer && {
        limitedTimeDiscountOffer
      })
    };
  } else if (dto.type === ServiceStatusType.Amended) {
    return {
      type: dto.type,
      date: timeModel.fromDateStampObject(dto.date),
      amended: {
        amendedOn: timeModel.fromDateStampObject(dto.amended.amendedOn),
        approvalMethod: ApprovalMethodDateModel.fromDTO(dto.amended.approvalMethod),
        note: dto.amended.note,
        changes: changesFromDTO(dto.amended.changes),
        effectiveDateSource: dto.amended.effectiveDateSource || null,
        effectiveDate: dto.amended.effectiveDate ? timeModel.fromDateStampObject(dto.amended.effectiveDate) : null
      }
    };
  } else if (dto.type === ServiceStatusType.Canceled) {
    return {
      type: dto.type
    };
  } else if (dto.type === ServiceStatusType.Completed) {
    const obj = {
      type: dto.type,
      date: timeModel.fromDateStampObject(dto.date),
      completed: {}
    };
    if (dto.completed.changes) {
      obj.completed.changes = changesFromDTO(dto.completed.changes);
    }
    return obj;
  } else if (dto.type === ServiceStatusType.Approved) {
    var _dto$approved, _dto$approved2;
    return {
      type: dto.type,
      date: timeModel.fromDateStampObject(dto.date),
      approved: {
        effectiveDateSource: ((_dto$approved = dto.approved) === null || _dto$approved === void 0 ? void 0 : _dto$approved.effectiveDateSource) || null,
        effectiveDate: (_dto$approved2 = dto.approved) !== null && _dto$approved2 !== void 0 && _dto$approved2.effectiveDate ? timeModel.fromDateStampObject(dto.approved.effectiveDate) : null
      }
    };
  } else {
    return {
      type: dto.type,
      date: timeModel.fromDateStampObject(dto.date)
    };
  }
}
export function statusToDTO(status, cost) {
  return selectors.matchStatus(status, {
    awaitingApproval: status => ({
      type: status.type,
      awaitingApproval: _objectSpread(_objectSpread(_objectSpread({
        addedOn: timeModel.toDateStampObject(status.awaitingApproval.addedOn),
        approvalMethod: status.awaitingApproval.approvalMethod.toDTO(),
        note: status.awaitingApproval.note
      }, status.awaitingApproval.limitedTimeDiscountOffer && cost.discount && {
        limitedTimeDiscountOffer: {
          discount: cost.discount.toDTO(),
          expiration: status.awaitingApproval.limitedTimeDiscountOffer.expiration
        }
      }), status.awaitingApproval.effectiveDate && {
        effectiveDate: timeModel.toDateStampObject(status.awaitingApproval.effectiveDate)
      }), status.awaitingApproval.effectiveDateSource && {
        effectiveDateSource: status.awaitingApproval.effectiveDateSource
      })
    }),
    amended: status => ({
      type: status.type,
      date: timeModel.toDateStampObject(status.date),
      amended: _objectSpread(_objectSpread({
        amendedOn: timeModel.toDateStampObject(status.amended.amendedOn),
        approvalMethod: status.amended.approvalMethod.toDTO(),
        note: status.amended.note,
        changes: changesToDTO(status.amended.changes)
      }, status.amended.effectiveDate && {
        effectiveDate: timeModel.toDateStampObject(status.amended.effectiveDate)
      }), status.amended.effectiveDateSource && {
        effectiveDateSource: status.amended.effectiveDateSource
      })
    }),
    canceled: status => ({
      type: status.type
    }),
    completed: status => {
      const dto = {
        type: status.type,
        date: timeModel.toDateStampObject(status.date),
        completed: {}
      };
      if (status.completed.changes) {
        dto.completed.changes = changesToDTO(status.completed.changes);
      }
      return dto;
    },
    terminated: status => ({
      type: status.type,
      date: timeModel.toDateStampObject(status.date)
    }),
    approved: status => {
      var _status$approved, _status$approved$effe;
      return _objectSpread({
        type: status.type,
        date: timeModel.toDateStampObject(status.date)
      }, ((_status$approved = status.approved) === null || _status$approved === void 0 ? void 0 : _status$approved.effectiveDate) && {
        approved: {
          effectiveDate: timeModel.toDateStampObject(status.approved.effectiveDate),
          effectiveDateSource: (_status$approved$effe = status.approved.effectiveDateSource) !== null && _status$approved$effe !== void 0 ? _status$approved$effe : undefined
        }
      });
    }
  });
}
export function toDBService(service) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    cost
  } = service;
  const startBillingAt = serviceModel.startBillingAt(service);
  return _objectSpread({
    id: service.id,
    name: service.name,
    description: service.description,
    billedUpon: serviceBillingModel.toDBServiceBillingTrigger(service.billing),
    cost: {
      pricingType: serviceCostModel.isFixedPrice(cost) ? {
        type: cost.pricingType,
        fixed: {
          price: utils.dollarsToCents(cost.price)
        },
        variable: null,
        range: null
      } : serviceCostModel.isVariablePrice(cost) ? {
        type: cost.pricingType,
        fixed: null,
        variable: {
          unitPrice: utils.dollarsToCents(cost.price),
          cap: cost.unitCap != null ? utils.dollarsToCents(cost.unitCap) : options.forceZeroCap ? 0 : null,
          name: cost.unitName
        },
        range: null
      } : {
        type: cost.pricingType,
        fixed: null,
        variable: null,
        range: {
          minPrice: cost.minPrice && utils.dollarsToCents(cost.minPrice),
          maxPrice: utils.dollarsToCents(cost.maxPrice)
        }
      },
      discount: (() => {
        const originalDiscount = cost.discount || cost.expiredDiscount;
        return originalDiscount ? originalDiscount.toDBDTO() : {
          type: DiscountDTOType.None
        };
      })(),
      priceIncrease: cost.priceIncrease.toDB()
    },
    note: options.note || null,
    status: selectors.isTerminated(service.status) || selectors.isCanceled(service.status) ? service.status.type : undefined,
    serviceTemplateId: service.serviceTemplateId,
    participation: !service.participation.type ? makers.makeMandatoryParticipation() : service.participation,
    bundle: service.bundle && {
      services: service.bundle.services.map(includedServiceToDTO)
    }
  }, startBillingAt && {
    effectiveDate: toDateStampObject(startBillingAt)
  });
}
function changesFromDTO(changesDTO) {
  return _objectSpread(_objectSpread({}, changesDTO), {}, {
    billing: changesDTO.billing && serviceBillingModel.fromDTO(changesDTO.billing) || null,
    cost: changesDTO.cost && serviceCostModel.fromDTO(changesDTO.cost),
    bundle: changesDTO.bundle && {
      services: changesDTO.bundle.services.map(includedServiceFromDTO)
    }
  });
}
function changesToDTO(changes) {
  return _objectSpread(_objectSpread({}, changes), {}, {
    cost: changes.cost ? serviceCostModel.toDTO({
      cost: changes.cost,
      shouldResetDiscount: false
    }) : null,
    billing: changes.billing ? serviceBillingModel.toDTO(changes.billing) : null,
    bundle: changes.bundle && {
      services: changes.bundle.services.map(includedServiceToDTO)
    }
  });
}